import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaUpload, FaTrash, FaImage, FaSpinner, FaCheck, FaTimes, FaUsers, FaCog, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Axios instance
const isDevelopment = process.env.NODE_ENV === 'development';

const axiosInstance = axios.create({
  baseURL: isDevelopment ? 'http://localhost:3001' : '',
  timeout: 60000, // 60 seconds for file uploads
  withCredentials: true, // Important: sends Basic Auth credentials
  headers: {
    'Connection': 'keep-alive'
  }
});

const HFBAdminPanel = () => {
  const [activeSection, setActiveSection] = useState('backgrounds');
  const [weddingHash, setWeddingHash] = useState('');
  const [currentBackground, setCurrentBackground] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [allBackgrounds, setAllBackgrounds] = useState([]);
  const [showAllBackgrounds, setShowAllBackgrounds] = useState(false);
  const [allWeddings, setAllWeddings] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [showCreateWeddingModal, setShowCreateWeddingModal] = useState(false);
  const [newWeddingPath, setNewWeddingPath] = useState('');
  const [newWeddingPassword, setNewWeddingPassword] = useState('');
  const [newWeddingEmail, setNewWeddingEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [deletionValidations, setDeletionValidations] = useState({});
  const [generatingToken, setGeneratingToken] = useState(null); // Track which wedding is generating token

  // Show message with auto-hide
  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Fetch deletion validation for a wedding
  const fetchDeletionValidation = async (weddingPath) => {
    try {
      const response = await axiosInstance.get(`/api/wedding/${weddingPath}/validate-deletion`);
      if (response.data.success) {
        setDeletionValidations(prev => ({
          ...prev,
          [weddingPath]: response.data
        }));
        return response.data;
      }
    } catch (error) {
      console.error(`Error fetching deletion validation for ${weddingPath}:`, error);
      return { canDelete: true }; // Default to allowing deletion if validation fails
    }
  };

  // Fetch all wedding hashes with detailed info including is_enabled
  const fetchAllWeddings = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch all wedding info (admin endpoint - includes hash data)
      const response = await axiosInstance.get('/api/admin/weddings');
      
      if (response.data.success) {
        setAllWeddings(response.data.data);
        
        // Fetch deletion validations for all weddings
        const validationPromises = response.data.data.map(wedding => 
          fetchDeletionValidation(wedding.weddingPath)
        );
        
        await Promise.all(validationPromises);
        
        showMessage(`Found ${response.data.data.length} weddings`);
      } else {
        showMessage('Error fetching weddings', 'error');
      }
    } catch (error) {
      console.error('Error fetching weddings:', error);
      showMessage('Error fetching weddings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Show confirmation modal for toggle action
  const showToggleConfirmation = (weddingPath, currentStatus) => {
    const newStatus = !currentStatus;
    const actionTurkish = newStatus ? 'enable' : 'disable';
    
    setConfirmationData({
      weddingPath,
      currentStatus,
      newStatus,
      actionTurkish,
      action: newStatus ? 'AÇMAK' : 'KAPATMAK'
    });
    setConfirmationInput('');
    setShowConfirmModal(true);
  };

  // Execute the toggle after confirmation
  const executeToggle = async () => {
    if (!confirmationData) return;
    
    const { weddingPath, newStatus } = confirmationData;
    
    try {
      setLoading(true);
      setShowConfirmModal(false);
      
      const response = await axiosInstance.put(`/api/wedding/${weddingPath}/toggle-enabled`, {
        enabled: newStatus
      });
      
      if (response.data.success) {
        // Update the local state
        setAllWeddings(prev => 
          prev.map(wedding => 
            wedding.weddingPath === weddingPath 
              ? { ...wedding, isEnabled: newStatus }
              : wedding
          )
        );
        
        showMessage(`✅ ${weddingPath} için upload ${newStatus ? 'AÇILDI' : 'KAPATILDI'}!`);
      } else {
        showMessage('Error updating wedding status', 'error');
      }
    } catch (error) {
      console.error('Error toggling wedding status:', error);
      showMessage('Error updating wedding status', 'error');
    } finally {
      setLoading(false);
      setConfirmationData(null);
      setConfirmationInput('');
    }
  };

  // Show delete confirmation
  const showDeleteConfirmation = (weddingPath) => {
    const validation = deletionValidations[weddingPath];
    
    if (!validation || !validation.canDelete) {
      const details = validation?.details || {};
      const mediaCount = details.media?.count || 0;
      const backgroundCount = details.background?.count || 0;
      
      let warningMessage = `Bu düğün silinemez:\n\n`;
      if (mediaCount > 0) {
        warningMessage += `📁 ${mediaCount} adet fotoğraf/video mevcut\n`;
      }
      if (backgroundCount > 0) {
        warningMessage += `🖼️ ${backgroundCount} adet background resmi mevcut\n`;
      }
      warningMessage += `\nÖnce bu dosyaları manuel olarak silin.`;
      
      alert(warningMessage);
      return;
    }
    
    setConfirmationData({
      weddingPath,
      action: 'SİLMEK',
      actionTurkish: 'delete',
      type: 'delete'
    });
    setConfirmationInput('');
    setShowConfirmModal(true);
  };

  // Execute wedding deletion
  const executeDelete = async () => {
    if (!confirmationData) return;
    
    const { weddingPath } = confirmationData;
    
    try {
      setLoading(true);
      setShowConfirmModal(false);
      
      const response = await axiosInstance.delete(`/api/wedding/${weddingPath}`);
      
      if (response.data.success) {
        // Remove from local state
        setAllWeddings(prev => prev.filter(wedding => wedding.weddingPath !== weddingPath));
        showMessage(`Wedding "${weddingPath}" başarıyla silindi`);
      } else {
        showMessage('Wedding silinirken hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Error deleting wedding:', error);
      showMessage('Wedding silinirken hata oluştu', 'error');
    } finally {
      setLoading(false);
      setConfirmationData(null);
      setConfirmationInput('');
    }
  };

  // Generate token and open gallery (for admin access)
  const handleViewGallery = async (weddingHash, weddingPath) => {
    setGeneratingToken(weddingHash);
    
    try {
      const response = await axiosInstance.post('/api/admin/generate-gallery-token', {
        weddingHash: weddingHash
      });
      
      if (response.data.success) {
        // Set token as cookie
        const token = response.data.token;
        document.cookie = `gallery_token=${token}; path=/; max-age=86400`; // 24 hours
        
        // Open gallery in new tab
        window.open(`/camera-roll/${weddingHash}`, '_blank');
        
        showMessage(`Galeri açılıyor: ${weddingPath}`);
      } else {
        showMessage(response.data.error || 'Token oluşturulamadı', 'error');
      }
    } catch (error) {
      console.error('Error generating gallery token:', error);
      showMessage('Galeri erişim hatası. Lütfen tekrar deneyin.', 'error');
    } finally {
      setGeneratingToken(null);
    }
  };

  // Create new wedding
  const createNewWedding = async () => {
    // Validation
    if (!newWeddingPath.trim()) {
      showMessage('Wedding path boş olamaz', 'error');
      return;
    }
    if (!newWeddingPassword.trim()) {
      showMessage('Şifre boş olamaz', 'error');
      return;
    }
    if (newWeddingPassword.length < 6) {
      showMessage('Şifre en az 6 karakter olmalıdır', 'error');
      return;
    }
    if (!newWeddingEmail.trim()) {
      showMessage('Email boş olamaz', 'error');
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newWeddingEmail.trim())) {
      showMessage('Geçerli bir email adresi giriniz', 'error');
      return;
    }

    try {
      setLoading(true);
      setShowCreateWeddingModal(false);
      
      const response = await axiosInstance.post('/api/wedding', {
        weddingPath: newWeddingPath.trim(),
        username: newWeddingPath.trim(), // Use wedding path as username
        password: newWeddingPassword.trim(),
        email: newWeddingEmail.trim()
      });
      
      if (response.data.success) {
        // Add to local state
        setAllWeddings(prev => [response.data.wedding, ...prev]);
        showMessage(`Wedding "${newWeddingPath}" başarıyla oluşturuldu`);
        // Clear all fields
        setNewWeddingPath('');
        setNewWeddingPassword('');
        setNewWeddingEmail('');
        setShowPassword(false);
      } else {
        showMessage(response.data.error || 'Wedding oluşturulurken hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Error creating wedding:', error);
      if (error.response?.status === 409) {
        showMessage('Bu wedding path zaten mevcut', 'error');
      } else if (error.response?.data?.error) {
        showMessage(error.response.data.error, 'error');
      } else {
        showMessage('Wedding oluşturulurken hata oluştu', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle confirmation modal
  const handleConfirmationSubmit = () => {
    if (!confirmationData) return;
    
    if (confirmationInput.toLowerCase().trim() !== confirmationData.actionTurkish.toLowerCase()) {
      showMessage(`Hatalı doğrulama! "${confirmationData.actionTurkish}" yazmanız gerekiyordu.`, 'error');
      return;
    }
    
    // Execute appropriate action based on type
    if (confirmationData.type === 'delete') {
      executeDelete();
    } else {
      executeToggle();
    }
  };

  const cancelConfirmation = () => {
    setShowConfirmModal(false);
    setConfirmationData(null);
    setConfirmationInput('');
    showMessage('İşlem iptal edildi', 'error');
  };

  // Fetch current background for a wedding hash
  const fetchCurrentBackground = async (hash) => {
    if (!hash) return;
    
    try {
      setLoading(true);
      console.log(`Fetching background for hash: ${hash}`);
      
      const response = await axiosInstance.get(`/api/backgrounds/${hash}`);
      
      if (response.data.success) {
        setCurrentBackground(response.data.data);
        showMessage(`Background found for wedding hash: ${hash}`);
      } else {
        setCurrentBackground(null);
        showMessage('No background found for this wedding', 'error');
      }
    } catch (error) {
      console.error('Error fetching background:', error);
      setCurrentBackground(null);
      if (error.response?.status === 404) {
        showMessage('No background found for this wedding', 'error');
      } else {
        showMessage('Error fetching background', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Upload new background
  const handleUpload = async () => {
    if (!weddingHash || !selectedFile) {
      showMessage('Please enter wedding hash and select a file', 'error');
      return;
    }

    try {
      setUploading(true);
      console.log(`Uploading background for hash: ${weddingHash}`);

      const formData = new FormData();
      formData.append('background', selectedFile);

      const response = await axiosInstance.post(
        `/api/backgrounds/${weddingHash}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setCurrentBackground(response.data.data);
        setSelectedFile(null);
        showMessage('Background uploaded successfully!');
        
        // Reset file input
        const fileInput = document.getElementById('background-file-input');
        if (fileInput) fileInput.value = '';
      } else {
        showMessage('Upload failed', 'error');
      }
    } catch (error) {
      console.error('Error uploading background:', error);
      showMessage(error.response?.data?.error || 'Error uploading background', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Delete current background
  const handleDelete = async () => {
    if (!weddingHash || !currentBackground) {
      showMessage('No background to delete', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this background?')) {
      return;
    }

    try {
      setLoading(true);
      console.log(`Deleting background for hash: ${weddingHash}`);

      const response = await axiosInstance.delete(`/api/backgrounds/${weddingHash}`);

      if (response.data.success) {
        setCurrentBackground(null);
        showMessage('Background deleted successfully!');
      } else {
        showMessage('Delete failed', 'error');
      }
    } catch (error) {
      console.error('Error deleting background:', error);
      showMessage(error.response?.data?.error || 'Error deleting background', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all backgrounds
  const fetchAllBackgrounds = async () => {
    try {
      setLoading(true);
      console.log('Fetching all backgrounds');

      const response = await axiosInstance.get('/api/backgrounds');

      if (response.data.success) {
        setAllBackgrounds(response.data.data);
        setShowAllBackgrounds(true);
        showMessage(`Found ${response.data.data.length} backgrounds`);
      } else {
        showMessage('Error fetching all backgrounds', 'error');
      }
    } catch (error) {
      console.error('Error fetching all backgrounds:', error);
      showMessage('Error fetching all backgrounds', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Silently preload backgrounds for status checks without toggling the grid UI
  const loadBackgroundsForStatus = async () => {
    try {
      const response = await axiosInstance.get('/api/backgrounds');
      if (response.data.success) {
        setAllBackgrounds(response.data.data);
      }
    } catch (error) {
      // No user-facing message; this is a silent preload
      console.error('Error preloading backgrounds for status:', error);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMessage('Please select an image file', 'error');
        e.target.value = '';
        return;
      }
      
      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        showMessage('File too large. Maximum size is 50MB', 'error');
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      showMessage(`Selected: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    }
  };

  // Handle search by wedding hash
  const handleSearch = () => {
    if (weddingHash.trim()) {
      fetchCurrentBackground(weddingHash.trim());
    } else {
      showMessage('Please enter a wedding hash', 'error');
    }
  };

  // Load all weddings on component mount
  useEffect(() => {
    fetchAllWeddings();
    loadBackgroundsForStatus();
  }, [fetchAllWeddings]);

  return (
    <div className="min-h-screen bg-gray-100">
      <LanguageSwitcher />
      
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">HFB Admin Panel</h1>
              <p className="text-gray-600 mt-1">Wedding Background Management System</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                <FaUsers className="inline mr-1" />
                {allWeddings.length} Weddings
              </div>
              <div className="text-sm text-green-600">
                <FaToggleOn className="inline mr-1" />
                {allWeddings.filter(w => w.isEnabled).length} Enabled
              </div>
              <div className="text-sm text-red-500">
                <FaToggleOff className="inline mr-1" />
                {allWeddings.filter(w => !w.isEnabled).length} Disabled
              </div>
              <div className="text-sm text-gray-500">
                <FaImage className="inline mr-1" />
                {allBackgrounds.length} Backgrounds
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveSection('backgrounds')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSection === 'backgrounds'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaImage className="inline mr-2" />
              Background Manager
            </button>
            <button
              onClick={() => setActiveSection('weddings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSection === 'weddings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaUsers className="inline mr-2" />
              Wedding List
            </button>
            <button
              onClick={() => setActiveSection('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSection === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaCog className="inline mr-2" />
              Settings
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            messageType === 'error' 
              ? 'bg-red-100 border border-red-400 text-red-700' 
              : 'bg-green-100 border border-green-400 text-green-700'
          }`}>
            <div className="flex items-center">
              {messageType === 'error' ? <FaTimes className="mr-2" /> : <FaCheck className="mr-2" />}
              {message}
            </div>
          </div>
        )}

        {/* Background Management Section */}
        {activeSection === 'backgrounds' && (
          <div className="space-y-6">
            {/* Wedding Hash Search */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Search by Wedding Hash</h2>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Enter wedding hash (e.g., 12345)"
                  value={weddingHash}
                  onChange={(e) => setWeddingHash(e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? <FaSpinner className="animate-spin" /> : 'Search'}
                </button>
              </div>
            </div>

            {/* Current Background Display */}
            {weddingHash && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Current Background for Hash: {weddingHash}
                </h2>
                
                {currentBackground ? (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <img
                        src={currentBackground.background_url}
                        alt="Current background"
                        className="w-full max-w-2xl h-64 object-cover rounded-lg mx-auto"
                      />
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 text-center">
                        <div>
                          <strong>File:</strong><br />
                          {currentBackground?.file_name?.split('/').pop() || 'N/A'}
                        </div>
                        <div>
                          <strong>Size:</strong><br />
                          {currentBackground?.file_size ? (currentBackground.file_size / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}
                        </div>
                        <div>
                          <strong>Type:</strong><br />
                          {currentBackground?.file_type || 'N/A'}
                        </div>
                        <div>
                          <strong>Uploaded:</strong><br />
                          {currentBackground?.created_at ? new Date(currentBackground.created_at).toLocaleDateString('tr-TR') : 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={handleDelete}
                      disabled={loading}
                      className="w-full px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                      Delete Background
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FaImage className="mx-auto text-5xl mb-4 opacity-50" />
                    <p className="text-lg">No background found for this wedding</p>
                  </div>
                )}
              </div>
            )}

            {/* Upload New Background */}
            {weddingHash && !currentBackground && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload New Background</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Image File (Max 50MB)
                    </label>
                    <input
                      id="background-file-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {selectedFile && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center">
                        <FaImage className="text-blue-500 mr-3" />
                        <div>
                          <p className="font-medium text-blue-900">{selectedFile.name}</p>
                          <p className="text-sm text-blue-600">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
                    {uploading ? 'Uploading...' : 'Upload Background'}
                  </button>
                </div>
              </div>
            )}

            {/* All Backgrounds */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">All Wedding Backgrounds</h2>
                <button
                  onClick={fetchAllBackgrounds}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? <FaSpinner className="animate-spin" /> : 'Refresh'}
                </button>
              </div>

              {showAllBackgrounds && allBackgrounds.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {allBackgrounds.map((bg) => (
                    <div key={bg.id} className="bg-gray-50 rounded-lg overflow-hidden">
                      <img
                        src={bg.background_url}
                        alt={`Background for ${bg.wedding_owner}`}
                        className="w-full h-40 object-cover"
                      />
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-2">{bg.wedding_owner}</h3>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p><strong>Hash:</strong> {bg.wedding_hash}</p>
                          <p><strong>Size:</strong> {(bg.file_size / 1024 / 1024).toFixed(2)} MB</p>
                          <p><strong>Uploaded:</strong> {new Date(bg.created_at).toLocaleDateString()}</p>
                        </div>
                        <button
                          onClick={() => {
                            setWeddingHash(bg.wedding_hash.toString());
                            setCurrentBackground(bg);
                            setShowAllBackgrounds(false);
                            setActiveSection('backgrounds');
                          }}
                          className="mt-3 w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors"
                        >
                          Manage This Wedding
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showAllBackgrounds && allBackgrounds.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FaImage className="mx-auto text-4xl mb-3 opacity-50" />
                  <p>No backgrounds found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Wedding List Section */}
        {activeSection === 'weddings' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Wedding Management</h2>
              <button
                onClick={() => setShowCreateWeddingModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <FaUsers className="text-sm" />
                <span>Create New Wedding</span>
              </button>
            </div>
            <div className="p-6">
              {allWeddings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Wedding Path
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Upload Status
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          BG
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allWeddings.map((wedding, index) => {
                        const hasBackground = allBackgrounds.some(bg => String(bg.wedding_hash) === String(wedding.hash));
                        return (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {wedding.weddingPath}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => showToggleConfirmation(wedding.weddingPath, wedding.isEnabled)}
                                  disabled={loading}
                                  className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium transition-colors border-2 ${
                                    wedding.isEnabled
                                      ? 'bg-green-100 text-green-800 hover:bg-green-200 border-green-300 hover:border-green-400'
                                      : 'bg-red-100 text-red-800 hover:bg-red-200 border-red-300 hover:border-red-400'
                                  } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                                  title={`Bu düğün için upload özelliğini ${wedding.isEnabled ? 'KAPAT' : 'AÇ'} (Doğrulama gereklidir)`}
                                >
                                  {wedding.isEnabled ? <FaToggleOn className="text-green-600" /> : <FaToggleOff className="text-red-600" />}
                                  <span className="font-semibold">
                                    {wedding.isEnabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
                                hasBackground 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-gray-100 text-gray-400'
                              }`} title={hasBackground ? 'Has Background' : 'No Background'}>
                                {hasBackground ? '✓' : '✗'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => {
                                    setWeddingHash(wedding.hash.toString());
                                    setActiveSection('backgrounds');
                                    fetchCurrentBackground(wedding.hash);
                                  }}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Manage Background
                                </button>
                                <a
                                  href={`/${wedding.weddingPath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:text-purple-900"
                                  title="Yükleme Ekranı"
                                >
                                  Upload Screen
                                </a>
                                <button
                                  onClick={() => handleViewGallery(wedding.hash, wedding.weddingPath)}
                                  disabled={generatingToken === wedding.hash}
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-wait"
                                  title="Web Camera Roll"
                                >
                                  {generatingToken === wedding.hash ? 'Açılıyor...' : 'View Gallery'}
                                </button>
                                <a
                                  href={`/camera-roll-mobile/${wedding.hash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-orange-600 hover:text-orange-900"
                                  title="Mobile Camera Roll"
                                >
                                  Mobile Album
                                </a>
                                {(() => {
                                  const validation = deletionValidations[wedding.weddingPath];
                                  const canDelete = validation?.canDelete !== false;
                                  const details = validation?.details || {};
                                  const mediaCount = details.media?.count || 0;
                                  const backgroundCount = details.background?.count || 0;
                                  
                                  let title = "Bu düğünü sil";
                                  let className = "text-red-600 hover:text-red-900 disabled:opacity-50";
                                  
                                  if (!canDelete) {
                                    title = `Silinemez: ${mediaCount} media, ${backgroundCount} background dosyası mevcut`;
                                    className = "text-gray-400 cursor-not-allowed";
                                  }
                                  
                                  return (
                                    <button
                                      onClick={() => showDeleteConfirmation(wedding.weddingPath)}
                                      disabled={loading || !canDelete}
                                      className={className}
                                      title={title}
                                    >
                                      <FaTrash className="inline mr-1" />
                                      {canDelete ? 'Delete' : 'Protected'}
                                    </button>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaUsers className="mx-auto text-4xl mb-3 opacity-50" />
                  <p>No weddings found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Section */}
        {activeSection === 'settings' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">System Settings</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">System Information</h3>
                <div className="text-sm text-blue-800 space-y-1">
                  <p>• Environment: {isDevelopment ? 'Development' : 'Production'}</p>
                  <p>• API Base URL: {axiosInstance.defaults.baseURL || 'Relative Path'}</p>
                  <p>• Max File Size: 50MB</p>
                  <p>• Supported Formats: JPEG, PNG, WebP, etc.</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={fetchAllWeddings}
                    className="w-full text-left px-3 py-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50"
                  >
                    <FaUsers className="inline mr-2" />
                    Refresh Wedding List
                  </button>
                  <button
                    onClick={fetchAllBackgrounds}
                    className="w-full text-left px-3 py-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50"
                  >
                    <FaImage className="inline mr-2" />
                    Refresh Background List
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && confirmationData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                    <span className="text-2xl font-bold text-yellow-600">!</span>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Güvenlik Doğrulaması
                  </h3>
                  <p className="text-sm text-gray-500">
                    Bu işlem geri alınamaz!
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800">
                    {confirmationData.type === 'delete' ? (
                      <>
                        <strong>"{confirmationData.weddingPath}"</strong> düğünü{' '}
                        <strong className="text-red-700">tamamen silmek</strong> istiyorsunuz.
                        <br />
                        <span className="text-red-600 font-medium">
                          ⚠️ Bu işlem geri alınamaz! Tüm veriler kaybolacak.
                        </span>
                      </>
                    ) : (
                      <>
                        <strong>"{confirmationData.weddingPath}"</strong> düğünü için upload özelliğini{' '}
                        <strong className={confirmationData.newStatus ? 'text-green-700' : 'text-red-700'}>
                          {confirmationData.action}
                        </strong>{' '}
                        istiyorsunuz.
                      </>
                    )}
                  </p>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Doğrulamak için <strong>"{confirmationData.actionTurkish}"</strong> yazın:
                  </label>
                  <input
                    type="text"
                    value={confirmationInput}
                    onChange={(e) => setConfirmationInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleConfirmationSubmit();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`"${confirmationData.actionTurkish}" yazın...`}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500">
                    * Büyük/küçük harf duyarlı değildir
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={cancelConfirmation}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  İptal Et
                </button>
                <button
                  onClick={handleConfirmationSubmit}
                  disabled={!confirmationInput.trim()}
                  className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    confirmationData.type === 'delete'
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : confirmationData.newStatus
                        ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                        : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {confirmationData.action}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Wedding Modal */}
      {showCreateWeddingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                    <FaUsers className="text-2xl text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Yeni Düğün Oluştur
                  </h3>
                  <p className="text-sm text-gray-500">
                    Yeni bir düğün path'i ekleyin
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    Düğün path'i benzersiz olmalı ve URL-friendly karakterler içermelidir.
                    <br />
                    <strong>Örnek:</strong> "fatma-mehmet-250101" veya "wedding-2025"
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Wedding Path (also serves as username) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wedding Path (Kullanıcı Adı): <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newWeddingPath}
                      onChange={(e) => setNewWeddingPath(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="düğün-path-adı"
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      * Bu alan hem URL hem de kullanıcı adı olarak kullanılacaktır
                    </p>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Şifre: <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newWeddingPassword}
                        onChange={(e) => setNewWeddingPassword(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="en az 6 karakter"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email: <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={newWeddingEmail}
                      onChange={(e) => setNewWeddingEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-xs text-gray-600">
                      <strong>Not:</strong> Dil (Türkçe) ve kart tasarımı (vertical-design1) otomatik olarak atanacaktır.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCreateWeddingModal(false);
                    setNewWeddingPath('');
                    setNewWeddingPassword('');
                    setNewWeddingEmail('');
                    setShowPassword(false);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  İptal Et
                </button>
                <button
                  onClick={createNewWedding}
                  disabled={!newWeddingPath.trim() || !newWeddingPassword.trim() || !newWeddingEmail.trim() || loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Oluşturuluyor...' : 'Oluştur'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HFBAdminPanel; 